// The planets and moons of the night sky: the painted bodies from the Godot game
// (Assets/textures/sky/celestial, packed into one 4x2 atlas), drawn as camera-facing discs pinned
// to the far plane, all in ONE draw call. Each disc is shaded as a sphere against the true sun
// (Godot's celestial_billboard.gdshader: a soft terminator whose dark side keeps a cool, faint
// read of the texture), wears a thin atmosphere rim where it has one, and sinks into the haze at
// the horizon. By day only the giant and the big moon show, as pale ghosts in the gold.

import * as THREE from 'three';
import { HAZE } from '../materials';
import { BODIES, skyDir } from './daynight';
import { sharedTexture } from '../warm';

/** The uniforms the bodies read (DayNight.U, or SpaceBackdrop's own set). */
export interface CelestialUniforms {
  uSkyRot: THREE.IUniform; uSunDisc: THREE.IUniform; uNight: THREE.IUniform; uDusk: THREE.IUniform;
}

/** GLSL: the night field (nebula band + three star layers) on the sky-frame direction ds;
 *  px = angular size of a pixel. Needs vn( vec2 ) and uTime declared before it. Shared by the
 *  world sky dome and SpaceBackdrop (the saloon car's windows). */
export const STARFIELD_GLSL = /* glsl */ `
float h13( vec3 p3 ) { p3 = fract( p3 * 0.1031 ); p3 += dot( p3, p3.zyx + 31.32 ); return fract( ( p3.x + p3.y ) * p3.z ); }
// one layer of stars: a hashed 3D cell grid on the sky sphere, each live cell holds one star at a
// jittered point projected onto the sphere; sized in pixels (px = angular size of a pixel)
vec3 starLayer( vec3 ds, float scale, float density, float px, float gain ) {
  vec3 c = floor( ds * scale );
  if ( h13( c ) > density ) return vec3( 0.0 );
  vec3 o = vec3( h13( c + 17.1 ), h13( c + 43.7 ), h13( c + 91.3 ) ) * 0.6 + 0.2;
  float dd = length( ds - normalize( c + o ) ) / px; // distance in pixels
  float b = h13( c + 7.7 );
  float sz = 0.32 + 1.1 * pow( b, 6.0 );
  float core = exp( -dd * dd / ( sz * sz * 0.5 ) ) + 0.12 * pow( b, 4.0 ) * exp( -dd * dd / ( sz * sz * 12.0 ) );
  float tw = 0.72 + 0.28 * sin( uTime * ( 1.3 + 3.7 * b ) + h13( c + 2.9 ) * 60.0 );
  vec3 tint = mix( vec3( 0.72, 0.84, 1.0 ), vec3( 1.0, 0.84, 0.66 ), h13( c + 3.3 ) );
  return tint * core * ( 0.08 + 2.4 * pow( b, 6.0 ) ) * tw * gain;
}
float vn3( vec3 p ) { return vn( p.xy + p.z * 1.7 ) * 0.5 + vn( p.zx * 1.13 + p.y * 2.3 + 11.0 ) * 0.5; }
vec3 starfield( vec3 ds, float px ) {
  vec3 c = vec3( 0.0 );
  // the nebula: a tilted band of dusty teal and violet with a warm core and dark lanes
  vec3 N = normalize( vec3( 0.42, 0.62, -0.66 ) );
  float bt = dot( ds, N );
  float band = exp( -bt * bt / 0.05 );
  float n1 = vn3( ds * 3.1 ) * 0.6 + vn3( ds * 7.3 + 4.0 ) * 0.4;
  float lanes = smoothstep( 0.5, 0.72, vn3( ds * 9.5 + 2.0 ) * 0.6 + vn3( ds * 21.0 ) * 0.4 );
  vec3 neb = mix( vec3( 0.04, 0.17, 0.22 ), vec3( 0.24, 0.09, 0.32 ), smoothstep( 0.3, 0.7, n1 ) );
  neb += vec3( 0.28, 0.2, 0.15 ) * pow( band, 3.0 ) * n1;
  c += neb * band * ( 0.35 + 0.9 * n1 ) * ( 1.0 - 0.4 * lanes * band ) * 1.25;
  // a far, soft magenta-free glow cloud off the band so the dome is never an empty field
  c += vec3( 0.08, 0.06, 0.13 ) * smoothstep( 0.45, 0.85, vn3( ds * 1.6 + 9.0 ) ) * 0.6;
  // stars: sparse bright ones, dense faint ones, thickening inside the band
  c += starLayer( ds, 60.0, 0.012, px, 1.0 );
  c += starLayer( ds, 150.0, 0.01 + 0.07 * band, px, 0.32 * ( 1.0 - 0.6 * lanes * band ) );
  c += starLayer( ds, 300.0, 0.006 + 0.16 * band * ( 1.0 - lanes ), px, 0.2 );
  return c;
}
`;

const VERT = /* glsl */ `
attribute vec3 aDir;    // sky-frame direction
attribute vec2 aCorner; // -1..1
attribute vec4 aCell;   // atlas uv rect: u0 v0 du dv
attribute vec4 aBody;   // x: tan(half angle), y: day visibility, z: terminator softness, w: atmosphere
uniform mat3 uSkyRot;
varying vec4 vCell; varying vec2 vP; varying vec3 vDirW; varying vec4 vBody;
void main() {
  vec3 dirW = normalize( uSkyRot * aDir );
  vDirW = dirW;
  vBody = aBody;
  // the quad is 1.3x the disc: the margin carries the atmosphere's glow
  vP = aCorner * 1.3;
  vCell = aCell;
  // a disc 1000 m out, built in view space so it always faces the camera
  vec4 c = viewMatrix * vec4( cameraPosition + dirW * 1000.0, 1.0 );
  c.xy += aCorner * 1.3 * aBody.x * 1000.0;
  vec4 p = projectionMatrix * c;
  gl_Position = p.xyww; // on the far plane: every island, range and cloud stands in front
}
`;
const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3 uSunDisc; uniform float uNight; uniform float uDusk;
uniform vec3 uHazeColor; uniform vec3 uSunHazeColor; uniform float uSpace;
varying vec4 vCell; varying vec2 vP; varying vec3 vDirW; varying vec4 vBody;
void main() {
  float r2 = dot( vP, vP );
  vec2 uv = vCell.xy + clamp( vP * 0.5 + 0.5, 0.0, 1.0 ) * vCell.zw;
  vec4 tex = r2 < 1.0 ? texture2D( uAtlas, uv ) : vec4( 0.0 );
  // fake-sphere normal in view space, turned to world to meet the sun
  vec3 nV = normalize( vec3( vP * 1.04, sqrt( max( 1.0 - r2 * 1.08, 0.0001 ) ) ) );
  vec3 nW = normalize( ( vec4( nV, 0.0 ) * viewMatrix ).xyz );
  float soft = vBody.z;
  // the true sun, lifted a little: at night it is under the horizon and would leave every body
  // lit only from below (a dark cap over a thin smile); this keeps real, turning phases
  vec3 L = normalize( vec3( uSunDisc.x, max( uSunDisc.y, -0.1 ) + 0.28, uSunDisc.z ) );
  // swung 75 degrees round the vertical: the bodies opposite the sun show a real dark side
  // (gibbous phases) instead of a flat, evenly lit full face
  L = vec3( L.x * 0.259 - L.z * 0.966, L.y, L.x * 0.966 + L.z * 0.259 );
  // and leaned toward the viewer, so most of each face is lit (gibbous, never a black ball)
  L = normalize( L - vDirW * 0.75 );
  float lit = smoothstep( -soft, soft, dot( nW, L ) );
  // dark side: a cool earthshine that keeps the texture readable, never a black hole in the sky
  vec3 shade = vec3( 0.12, 0.13, 0.2 );
  // sit behind the air: a little less saturation and contrast than the raw paintings
  vec3 tx = mix( vec3( dot( tex.rgb, vec3( 0.299, 0.587, 0.114 ) ) ), tex.rgb, 0.78 );
  tx = mix( vec3( 0.18 ), tx, 0.85 );
  vec3 col = tx * mix( shade, vec3( 1.2 ), lit );
  // a touch of the print: soft posterize so the paintings share the world's stepped tones
  col = mix( col, floor( col * 7.0 + 0.5 ) / 7.0, 0.3 );
  // atmosphere rim on the lit limb (gas giant), a little glow around it too
  float limb = smoothstep( 0.55, 1.0, sqrt( r2 ) );
  col += vec3( 0.55, 0.75, 1.0 ) * limb * lit * vBody.w * 0.55;
  // the horizon haze swallows anything low, the dusk warms the lit side
  float e = vDirW.y + vP.y * vBody.x; // this pixel's elevation
  float vis = mix( vBody.y, 1.0, uNight ) * mix( smoothstep( -0.01, 0.14, e ), 1.0, uSpace );
  col *= mix( vec3( 1.0 ), vec3( 1.15, 0.86, 0.72 ), uDusk * 0.6 );
  // by day: a pale ghost, haze-coloured but with the bands ghosting through
  float luma = dot( tex.rgb, vec3( 0.299, 0.587, 0.114 ) );
  vec3 ghost = mix( uHazeColor, uSunHazeColor, 0.4 ) * ( 0.78 + 0.6 * luma * lit ) + vec3( 0.06 ) * lit;
  col = mix( ghost, col, smoothstep( 0.15, 0.85, uNight ) );
  // a thin veil of the night haze near the horizon
  // extinction: the lower limb sinks into the night air, and so does anything low
  // (in open space, SpaceBackdrop, there is no air to sink into)
  col = mix( col, uHazeColor * 1.3, ( 1.0 - smoothstep( 0.02, 0.5, e ) ) * 0.75 * ( 1.0 - uSpace ) );
  col = mix( col, uHazeColor * 1.3, ( 0.2 + 0.3 * smoothstep( 0.0, -0.9, vP.y ) ) * ( 1.0 - uSpace ) );
  // a soft halo of lit air round the bodies that have an atmosphere (and a hint on the rest)
  float rr = sqrt( r2 );
  vec3 nE = normalize( vec3( vP / max( rr, 1e-3 ), 0.0 ) );
  float haloLit = smoothstep( -0.3, 0.6, dot( normalize( ( vec4( nE, 0.0 ) * viewMatrix ).xyz ), L ) );
  float halo = exp( -max( rr - 0.97, 0.0 ) * 14.0 ) * smoothstep( 0.9, 1.0, rr ) * ( 0.12 + vBody.w ) * haloLit;
  vec3 haloC = mix( mix( uHazeColor * 2.2, vec3( 0.3, 0.4, 0.7 ), uSpace ), vec3( 0.5, 0.7, 1.0 ), 0.5 );
  float a = tex.a * vis * smoothstep( 1.0, 0.93, rr ); // a soft limb, not a cut-out edge
  float ha = halo * vis * 0.55 * ( 1.0 - a ) * uNight;
  gl_FragColor = vec4( ( col * a + haloC * ha ) / max( a + ha, 1e-3 ), a + ha );
}
`;

export class Celestial {
  readonly mesh: THREE.Mesh;
  private geo: THREE.BufferGeometry;
  private mat: THREE.ShaderMaterial;
  private tex: THREE.Texture;

  /** space: true for a view from open space (no horizon, no haze), e.g. a train window */
  constructor(u: CelestialUniforms, quality: number, space = false) {
    const bodies = quality <= 0 ? BODIES.slice(0, 3) : BODIES;
    const n = bodies.length;
    const dir = new Float32Array(n * 4 * 3), corner = new Float32Array(n * 4 * 2), cell = new Float32Array(n * 4 * 4), body = new Float32Array(n * 4 * 4);
    const idx: number[] = [];
    const d = new THREE.Vector3();
    bodies.forEach((b, i) => {
      skyDir(b.el, b.az, d);
      const u0 = (b.cell % 4) / 4, v0 = 1 - (Math.floor(b.cell / 4) + 1) / 2;
      const half = Math.tan((b.size / 2) * Math.PI / 180);
      const cs = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
      for (let k = 0; k < 4; k++) {
        const v = i * 4 + k;
        dir.set([d.x, d.y, d.z], v * 3);
        corner.set(cs[k], v * 2);
        cell.set([u0, v0, 0.25, 0.5], v * 4);
        body.set([half, b.dayVis, b.soft, b.atmo], v * 4);
      }
      // draw order follows the list: the giant first, the small bodies over it
      idx.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(dir.slice(), 3)); // unused by the shader; keeps three happy
    g.setAttribute('aDir', new THREE.BufferAttribute(dir, 3));
    g.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
    g.setAttribute('aCell', new THREE.BufferAttribute(cell, 4));
    g.setAttribute('aBody', new THREE.BufferAttribute(body, 4));
    g.setIndex(idx);
    this.geo = g;
    this.tex = sharedTexture('/assets/sky/celestial_atlas.webp', 'srgb1', (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 1; });
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false,
      uniforms: {
        uAtlas: { value: this.tex },
        uSkyRot: u.uSkyRot, uSunDisc: u.uSunDisc, uNight: u.uNight, uDusk: u.uDusk,
        uHazeColor: HAZE.uHazeColor, uSunHazeColor: HAZE.uSunHazeColor, uSpace: { value: space ? 1 : 0 },
      },
    });
    this.mesh = new THREE.Mesh(g, this.mat);
    this.mesh.frustumCulled = false;
    // transparent list: after every opaque island/range/cloud (depth-tested against them), and
    // before the mist bands (-79), which drift in front of a low planet
    this.mesh.renderOrder = -95;
    this.mesh.name = 'celestial';
  }

  dispose() { this.geo.dispose(); this.mat.dispose(); this.tex.dispose(); }
}
